import { alpha } from '@mui/material';
import { createContext } from 'react';
import { INK } from 'theme';

export const DEFAULT_LOTTERY_ID = "powerball";

const LOTTERY_ID_ALIASES = new Map([
  ["powerball", "powerball"],
  ["megamillions", "megamillions"],
  ["euromillions", "euromillions"],
  ["eurojackpot", "eurojackpot"],
  ["southafricapowerball", "southafricapowerball"],
  ["southafricapowerballplus", "southafricapowerballplus"],
  ["southafricapowerballxtra", "southafricapowerballplus"],
]);

const EUROPEAN_COUNTRY_CODES = new Set([
  "at", "be", "bg", "ch", "cy", "cz", "de", "dk", "ee", "es", "fi", "fr", "gb", "gr", "hr",
  "hu", "ie", "is", "it", "lt", "lu", "lv", "mt", "nl", "no", "pl", "pt", "ro", "se", "si",
  "sk",
]);

const normalizeLotteryId = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const normalized = value.toLowerCase().trim().replace(/[^a-z0-9]/g, "");
  return LOTTERY_ID_ALIASES.get(normalized) || "";
};

export class LotteryRules {
  constructor({
    id = "",
    name = "",
    description = "",
    maxNumber = 0,
    numbersToDraw = 0,
    extraBallMax = 0,
    extraBallMaxNumberToDraw = 0,
    totalCombinations = 0,
    extraBallTotalCombinations = 0,
    extraBallDisplayLabel = "",
    country = "",
    storageCountry = "",
    region = "",
    routeSlug = "",
    backendKey = "",
    defaultLocaleHints = [],
    defaultTimezoneHints = [],
    extraBallPalette = null,
  } = {}) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.maxNumber = maxNumber;
    this.numbersToDraw = numbersToDraw;
    this.extraBallMax = extraBallMax;
    this.extraBallMaxNumberToDraw = extraBallMaxNumberToDraw;
    this.totalCombinations = totalCombinations;
    this.extraBallTotalCombinations = extraBallTotalCombinations;
    this.extraBallDisplayLabel = extraBallDisplayLabel;
    this.country = country;
    this.storageCountry = storageCountry;
    this.region = region;
    this.routeSlug = routeSlug;
    this.backendKey = backendKey || id;
    this.defaultLocaleHints = defaultLocaleHints;
    this.defaultTimezoneHints = defaultTimezoneHints;
    this.extraBallPalette = extraBallPalette;
  }
}

export class PowerBallRules extends LotteryRules {

  constructor() {
    super({
      id: "powerball",
      name: "Powerball",
      description: "Standard Power Ball rules.",
      maxNumber: 69,
      numbersToDraw: 5,
      extraBallMax: 26,
      extraBallMaxNumberToDraw: 1,
      totalCombinations: 11_238_513,
      extraBallDisplayLabel: "Powerball",
      country: "United States",
      storageCountry: "us",
      region: "americas",
      routeSlug: "powerball",
      backendKey: "powerball",
      defaultLocaleHints: ["en-us"],
      defaultTimezoneHints: ["America/"],
    });
  }

}

export class MegaMillionsRules extends LotteryRules {

  constructor() {
    super({
      id: "megamillions",
      name: "Mega Millions",
      description: "Standard Mega Millions rules.",
      maxNumber: 70,
      numbersToDraw: 5,
      extraBallMax: 25, // Mega Ball
      extraBallMaxNumberToDraw: 1,
      totalCombinations: 12_103_014, // C(70,5)
      extraBallDisplayLabel: "Mega Ball",
      country: "United States",
      storageCountry: "us",
      region: "americas",
      routeSlug: "megamillions",
      backendKey: "megamillions",
      defaultLocaleHints: ["en-us"],
      defaultTimezoneHints: ["America/"],
    });
  }

}


export class EuroMillionsRules extends LotteryRules {

  constructor() {
    super({
      id: "euromillions",
      name: "EuroMillions",
      description: "Standard EuroMillions rules.",
      maxNumber: 50,
      numbersToDraw: 5,
      extraBallMax: 12, // Lucky Stars
      extraBallMaxNumberToDraw: 2,
      totalCombinations: 2_118_760, // C(50,5)
      extraBallTotalCombinations: 66, // C(12,2)
      extraBallDisplayLabel: "Lucky Star",
      country: "Europe",
      storageCountry: "eu",
      region: "europe",
      routeSlug: "euromillions",
      backendKey: "euromillions",
      defaultLocaleHints: ["fr-fr", "es-es", "pt-pt", "en-ie"],
      defaultTimezoneHints: ["Europe/Paris", "Europe/Madrid", "Europe/Dublin"],
      extraBallPalette: {
        primary: "#E6C15A",
        hover: "#F0CF6A",
        active: "#D4AF37",
        glow: "rgba(230, 193, 90, 0.25)",
        text: alpha(INK, .92)
        // text: "#FFFFFF",
        // textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
      },
    });
  }

}

export class EuroJackpotRules extends LotteryRules {

  constructor() {
    super({
      id: "eurojackpot",
      name: "EuroJackpot",
      description: "Standard EuroJackpot rules.",
      maxNumber: 50,
      numbersToDraw: 5,
      extraBallMax: 12, // Euro numbers
      extraBallMaxNumberToDraw: 2,
      totalCombinations: 2_118_760, // C(50,5)
      extraBallTotalCombinations: 66, // C(12,2)
      extraBallDisplayLabel: "Euro numbers",
      country: "Europe",
      storageCountry: "eu",
      region: "europe",
      routeSlug: "eurojackpot",
      backendKey: "eurojackpot",
      defaultLocaleHints: ["de-de", "fi-fi", "nl-nl", "sv-se", "it-it"],
      defaultTimezoneHints: ["Europe/Berlin", "Europe/Helsinki", "Europe/Amsterdam"],
      extraBallPalette: {
        primary: "#E6C15A",
        hover: "#F0CF6A",
        active: "#D4AF37",
        glow: "rgba(230, 193, 90, 0.25)",
        // text: "#FFFFFF",
        // textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
        text: alpha(INK, .92)
      },
    });
  }

}

export class SouthAfricaPowerBallRules extends LotteryRules {

  constructor() {
    super({
      id: "southafricapowerball",
      name: "South Africa PowerBall",
      description: "Standard South Africa PowerBall rules.",
      maxNumber: 50,
      numbersToDraw: 5,
      extraBallMax: 16,
      extraBallMaxNumberToDraw: 1,
      totalCombinations: 2_118_760,
      extraBallTotalCombinations: 16,
      extraBallDisplayLabel: "PowerBall",
      country: "South Africa",
      storageCountry: "za",
      region: "africa",
      routeSlug: "south-africa-powerball",
      backendKey: "southafricapowerball",
      defaultLocaleHints: ["en-za"],
      defaultTimezoneHints: ["Africa/Johannesburg"],
    });
  }

}

export class SouthAfricaPowerBallPlusRules extends LotteryRules {

  constructor() {
    super({
      id: "southafricapowerballplus",
      name: "South Africa PowerBall Xtra",
      description: "Standard South Africa PowerBall Xtra rules.",
      maxNumber: 50,
      numbersToDraw: 5,
      extraBallMax: 16,
      extraBallMaxNumberToDraw: 1,
      totalCombinations: 2_118_760,
      extraBallTotalCombinations: 16,
      extraBallDisplayLabel: "PowerBall",
      country: "South Africa",
      storageCountry: "za",
      region: "africa",
      routeSlug: "south-africa-powerball-xtra",
      backendKey: "southafricapowerballplus",
      defaultLocaleHints: ["en-za"],
      defaultTimezoneHints: ["Africa/Johannesburg"],
    });
  }

}

const LOTTERY_REGISTRY = Object.freeze({
  powerball: {
    id: "powerball",
    region: "americas",
    country: "United States",
    routeSlug: "powerball",
    backendKey: "powerball",
    labelKey: "lotteries.powerball",
    countryKey: "lotteries.countries.unitedStates",
    createRules: () => new PowerBallRules(),
  },
  megamillions: {
    id: "megamillions",
    region: "americas",
    country: "United States",
    routeSlug: "megamillions",
    backendKey: "megamillions",
    labelKey: "lotteries.megamillions",
    countryKey: "lotteries.countries.unitedStates",
    createRules: () => new MegaMillionsRules(),
  },
  euromillions: {
    id: "euromillions",
    region: "europe",
    country: "Europe",
    routeSlug: "euromillions",
    backendKey: "euromillions",
    labelKey: "lotteries.euromillions",
    countryKey: "lotteries.countries.europe",
    createRules: () => new EuroMillionsRules(),
  },
  eurojackpot: {
    id: "eurojackpot",
    region: "europe",
    country: "Europe",
    routeSlug: "eurojackpot",
    backendKey: "eurojackpot",
    labelKey: "lotteries.eurojackpot",
    countryKey: "lotteries.countries.europe",
    createRules: () => new EuroJackpotRules(),
  },
  southafricapowerball: {
    id: "southafricapowerball",
    region: "africa",
    country: "South Africa",
    routeSlug: "south-africa-powerball",
    backendKey: "southafricapowerball",
    labelKey: "lotteries.southAfricaPowerBall",
    countryKey: "lotteries.countries.southAfrica",
    createRules: () => new SouthAfricaPowerBallRules(),
  },
  southafricapowerballplus: {
    id: "southafricapowerballplus",
    region: "africa",
    country: "South Africa",
    routeSlug: "south-africa-powerball-xtra",
    backendKey: "southafricapowerballplus",
    labelKey: "lotteries.southAfricaPowerBallXtra",
    countryKey: "lotteries.countries.southAfrica",
    createRules: () => new SouthAfricaPowerBallPlusRules(),
  },
});

const LOTTERY_ORDER = Object.freeze([
  "powerball",
  "megamillions",
  "euromillions",
  "eurojackpot",
  "southafricapowerball",
  "southafricapowerballplus",
]);
const ROUTE_SLUG_TO_LOTTERY_ID = new Map(
  LOTTERY_ORDER.map((id) => [LOTTERY_REGISTRY[id].routeSlug.toLowerCase(), id])
);

export const getSupportedLotteries = () => (
  LOTTERY_ORDER.map((id) => LOTTERY_REGISTRY[id])
);

export const resolveLotteryId = (value, fallback = DEFAULT_LOTTERY_ID) => (
  normalizeLotteryId(value) || fallback
);

export const resolveLotteryIdFromRouteSlug = (value, fallback = "") => {
  if (typeof value !== "string") {
    return fallback;
  }

  const normalizedSlug = value.toLowerCase().trim();
  return ROUTE_SLUG_TO_LOTTERY_ID.get(normalizedSlug) || fallback;
};

export const getLotteryById = (value) => {
  const normalizedId = normalizeLotteryId(value) || DEFAULT_LOTTERY_ID;
  return LOTTERY_REGISTRY[normalizedId] || LOTTERY_REGISTRY[DEFAULT_LOTTERY_ID];
};

export const getLotteryRoutePath = (locale, lotteryId) => {
  const normalizedLocale = typeof locale === "string" && locale.trim() !== ""
    ? locale.toLowerCase().trim()
    : "en";
  const lottery = getLotteryById(lotteryId);
  return `/${normalizedLocale}/${lottery.routeSlug}`;
};

export const getLotteryRulesById = (value) => {
  const lottery = getLotteryById(value);
  return lottery.createRules();
};

const getNavigatorLocaleCandidates = () => {
  if (typeof navigator === "undefined") {
    return [];
  }

  const languages = Array.isArray(navigator.languages) ? navigator.languages : [];
  const primary = typeof navigator.language === "string" ? [navigator.language] : [];
  return [...languages, ...primary]
    .filter((locale) => typeof locale === "string" && locale.trim() !== "")
    .map((locale) => locale.toLowerCase());
};

const isEuropeanLocale = (locale) => {
  if (typeof locale !== "string") {
    return false;
  }

  const countryCode = locale.split("-")[1];
  if (!countryCode) {
    return false;
  }

  return EUROPEAN_COUNTRY_CODES.has(countryCode.toLowerCase());
};

export const resolveLotteryIdFromEnvironment = (cachedLotteryId) => {
  const cached = normalizeLotteryId(cachedLotteryId);
  if (cached) {
    return cached;
  }

  const locales = getNavigatorLocaleCandidates();
  const matchedUS = locales.some((locale) => locale.startsWith("en-us"));
  if (matchedUS) {
    return "powerball";
  }

  const matchedEuropeanLocale = locales.some(isEuropeanLocale);
  if (matchedEuropeanLocale) {
    return "eurojackpot";
  }

  const timezone = typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "";
  if (typeof timezone === "string" && timezone.startsWith("Europe/")) {
    return "eurojackpot";
  }

  if (timezone === "Africa/Johannesburg") {
    return "southafricapowerball";
  }

  return DEFAULT_LOTTERY_ID;
};

// Make sure the shape of the default value matches what consumers expect
export const GameContext = createContext({
  gameRules: new PowerBallRules(),
  selectedLotteryId: DEFAULT_LOTTERY_ID,
  supportedLotteries: getSupportedLotteries(),
  setGameRules: () => { },
  setSelectedLotteryId: () => { },
});


export class User {
  static validAccessTiers = new Set(["public", "public_plus", "premium"]);
  static validSubscriptionStatuses = new Set(["active", "past_due", "canceled"]);
  static validSubscriptionIntervals = new Set(["monthly", "yearly"]);

  static normalizeAccessTier = (tier) => {
    const normalizedTier = String(tier || "").toLowerCase();
    return this.validAccessTiers.has(normalizedTier) ? normalizedTier : "public";
  };

  static getAccessTier = (user) => (
    this.normalizeAccessTier(user?.access?.tier || user?.tier)
  );

  static normalizeSubscriptionStatus = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();
    return this.validSubscriptionStatuses.has(normalizedStatus) ? normalizedStatus : "";
  };

  static normalizeSubscriptionInterval = (interval) => {
    const normalizedInterval = String(interval || "").toLowerCase();
    return this.validSubscriptionIntervals.has(normalizedInterval) ? normalizedInterval : "";
  };

  static getSubscriptionStatus = (user) => (
    this.normalizeSubscriptionStatus(user?.subscription?.status)
  );

  static getSubscriptionInterval = (user) => (
    this.normalizeSubscriptionInterval(user?.subscription?.interval)
  );

  static hasActiveSubscription = (user) => (
    this.getSubscriptionStatus(user) === "active"
      && this.getSubscriptionInterval(user) !== ""
  );

  static hasExtendedHistoryAccess = (user) => {
    if (!isAuthenticatedUser(user)) {
      return false;
    }

    const tier = this.getAccessTier(user);
    return tier === "public_plus" || tier === "premium";
  };

  static getDrawHistoryLockReason = (user) => {
    if (!isAuthenticatedUser(user)) {
      return "trial";
    }

    return this.hasExtendedHistoryAccess(user) ? null : "subscription";
  };

  constructor(idOrOptions, name, email, picture, settings, signOut) {
    const defaultPreferences = {
      draw_history_limit: {},
      draw_history_limit_by_lottery: {},
      selected_lottery: DEFAULT_LOTTERY_ID,
      country: "us",
      language: "en",
      theme: "system",
      // history: {
      //   batchSize: 25
      // }
    };

    if (typeof idOrOptions === "object" && idOrOptions !== null) {
      this.id = idOrOptions.id ?? idOrOptions.user_id ?? null;
      this.user_id = idOrOptions.user_id ?? idOrOptions.id ?? null;
      this.email = idOrOptions.email ?? null;
      this.name = idOrOptions.name ?? null;
      this.created_at = idOrOptions.created_at ?? null;
      this.updated_at = idOrOptions.updated_at ?? null;
      this.customer_since = idOrOptions.customer_since ?? null;
      this.picture = idOrOptions.picture ?? null;
      this.settings = idOrOptions.settings ?? null;
      this.trial = (
        idOrOptions.trial && typeof idOrOptions.trial === "object"
          ? idOrOptions.trial
          : null
      );
      this.market = (
        idOrOptions.market && typeof idOrOptions.market === "object"
          ? idOrOptions.market
          : null
      );
      this.signOut = idOrOptions.signOut ?? null;
      this.preferences = {
        ...defaultPreferences,
        ...(idOrOptions.preferences && typeof idOrOptions.preferences === "object"
          ? idOrOptions.preferences
          : {}),
      };
      this.access = {
        tier: this.constructor.normalizeAccessTier(idOrOptions?.access?.tier ?? idOrOptions?.tier),
        source: idOrOptions?.access?.source ?? null,
        expires_at: idOrOptions?.access?.expires_at ?? null,
      };
      this.subscription = (
        idOrOptions.subscription && typeof idOrOptions.subscription === "object"
          ? idOrOptions.subscription
          : {}
      );
      return;
    }

    this.id = idOrOptions ?? null;
    this.user_id = idOrOptions ?? null;
    this.name = name ?? null;
    this.email = email ?? null;
    this.created_at = null;
    this.updated_at = null;
    this.customer_since = null;
    this.picture = picture ?? null;
    this.settings = settings ?? null;
    this.trial = null;
    this.market = null;
    this.signOut = signOut ?? null;
    this.preferences = defaultPreferences;
    this.access = {
      tier: "public",
      source: null,
      expires_at: null,
    };
    this.subscription = {};
  }
}

export const createGuestUser = (overrides = {}) => new User({
  user_id: null,
  access: { tier: "public" },
  ...(overrides && typeof overrides === "object" ? overrides : {}),
});

export const isAuthenticatedUser = (user) =>
  Boolean(user?.id || user?.user_id) && typeof user?.signOut === "function";

export const UserContext = createContext({
  user: createGuestUser(),
  userResolved: true,
  setUser: () => { } // Include the default updater function
});

export const ThemeContext = createContext({
  themeMode: "system",
  setThemeMode: () => { }
});

export const LoginContext = createContext({
  triggerLogin: false,
  setTriggerLogin: () => { }
});



// export default { PowerBallRules, GameContext };
